/**
 * TestAuthoringTemplateDialog — create / edit a Test Authoring Template
 * (app-scoped). Both the Start (TestRunner) page and any future
 * configuration surface render the same editor.
 *
 * History — this lived in `pages/Templates.tsx` from #181 until the
 * Templates page was retired in favor of inline CRUD on the Start
 * page. Renamed from `GenerationTemplateDialog` under #687: the
 * template drives plan → generate → execute → heal (in discover or
 * resume mode), so "generation" undersold it.
 *
 * #687 also removed the client-side AI-Hints primer (`composePrimer` /
 * `HINT_PRIMERS` / the APP CONTEXT block): the variant's application
 * context is already injected server-side into every agent's system
 * prompt (`applicationContext.ts`), so stitching it into the Agent
 * Prompt textarea here was a duplicate — added by #242, then
 * superseded when the server-side block landed. The Agent Prompt
 * textarea is now a clean user-authored field, and the Test Type
 * selector (which only ever drove the primer) was dropped with it.
 */
import { useEffect, useState } from 'react'
import { Info } from 'lucide-react'
import {
  Button,
  Label,
  Input,
  Select,
  Textarea,
  Checkbox,
  Tooltip,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@/components/ui'

/** #1076 — Assertion timeout input row. Rendered in both the
 * discover-mode and resume-mode branches of this dialog (each
 * branch carries its own copy of the page-timeout row), so the
 * snippet is factored out here to keep the two surfaces in
 * lockstep. Surfaces the value in SECONDS to match the sibling
 * Page timeout row — operators reading "Assertion timeout: 5000"
 * next to "Page timeout: 10" couldn't tell whether the units were
 * mismatched or mistyped. The schema and Playwright API still live
 * in ms; the caller converts s→ms at save time and ms→s at init
 * (see the dialog's `useState` hooks). Range [1, 60] mirrors the
 * prior [1000, 60000] ms spec — 0 would mean "no timeout" to
 * Playwright, which is almost never what the operator meant. */
function AssertionTimeoutRow({ value, onChange }: { value: number; onChange: (next: number) => void }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs flex items-center gap-1.5">
        Assertion timeout (sec)
        <Tooltip
          content="Per-assertion polling timeout (await expect(...) calls). Tune up for slow-rendering / lazy-loading apps. Default 5."
          triggerLabel="What is the assertion timeout?"
          // AssertionTimeoutRow renders in column 1 (left) of the
          // discover-mode and resume-mode grids in this dialog
          // (it's the 3rd grid child in each branch). `align='start'`
          // anchors the popover to the trigger's left edge so it
          // opens rightward, away from the dialog's left edge where
          // a centered popover would clip.
          align="start"
        >
          <Info className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
        </Tooltip>
      </Label>
      <Input
        type="number"
        min={1}
        max={60}
        step={1}
        placeholder="5"
        value={value}
        onChange={e => {
          // Accept any non-negative integer during typing —
          // clamping to [1, 60] mid-type breaks the
          // "clear-then-type-larger-number" flow. The native
          // `min` / `max` attrs surface UI validation; final
          // clamp happens on blur (below) so the committed
          // value before save is guaranteed in-range.
          const parsed = parseInt(e.target.value, 10)
          onChange(Number.isFinite(parsed) && parsed >= 0 ? parsed : 0)
        }}
        onBlur={() => {
          // Snap to the allowed range on blur — operator sees
          // the final committed value before saving.
          onChange(Math.min(Math.max(value || 5, 1), 60))
        }}
        className="w-28"
      />
    </div>
  )
}
import { applicationsAPI, runTemplatesAPI } from '@/lib/api'
import { useMaxTestCasesPerRun } from '@/hooks/useRunLimits'
import { useToast } from '@/hooks/useToast'
import { toUserMessage, extractRawDetails } from '@/lib/userMessage'
import { useAsyncAction } from '@/hooks/useAsyncAction'
import { useRunTemplateLaunch } from '@/hooks/useRunTemplateLaunch'
import { RunTemplateLaunchFooter } from '@/components/RunTemplateLaunchFooter'
import type { RunTemplate, RunTemplateSettings, UpdateRunTemplateInput, UnifiedRunTemplate, PipelinePhase, LogLevel, StabilityExpectation, CandidateTest, CandidateScriptedTier } from '@/types'
import { CandidatePickerPanel } from '@/components/CandidatePickerPanel'
import { BetweenTestsPauseRow } from '@/components/BetweenTestsPauseRow'
import { BetweenStepsPauseRow } from '@/components/BetweenStepsPauseRow'
import { PromptHints } from '@/components/PromptHints'
import { PromptExamples } from '@/components/PromptExamples'
import {
  DEFAULT_INTER_TEST_DELAY_MS,
  DEFAULT_PLAYWRIGHT_TIMEOUT_MS,
  DEFAULT_SLOW_MOTION_MS,
} from '@ottotester/shared-types'

/** Feature Status options (#687) — the two `stabilityExpectation`
 * values, their radio labels, and the hover-tooltip copy. The tooltip
 * text is user-facing, so it uses the `OttoTester` brand spelling. */
const FEATURE_STATUS_OPTIONS: ReadonlyArray<{
  value: StabilityExpectation
  label: string
  tip: string
}> = [
  {
    value: 'established',
    label: 'Existing feature (already working in production)',
    tip: 'Already working in production. OttoTester will repair brittle tests aggressively.',
  },
  {
    value: 'volatile',
    label: 'New or recently changed feature',
    tip: 'Product behavior may still be evolving. OttoTester will stay conservative as a failure might be a genuine behavior change.',
  },
]

/** Parse a numeric input value and clamp it to [min, max]. NaN /
 * non-finite parses fall back to `fallback` (which is itself returned
 * un-clamped — callers pass a sane default within range). Used by the
 * resume-mode and discover-mode timeout inputs so large/negative
 * values can't sneak through the `parseInt(...) || N` pattern. */
function clampNum(value: string, min: number, max: number, fallback: number): number {
  const parsed = parseInt(value, 10)
  if (!Number.isFinite(parsed)) return fallback
  return Math.min(Math.max(parsed, min), max)
}

export function TestAuthoringTemplateDialog({ template, allTemplates: _allTemplates, appId, variantId, onClose, onSaved, launch, pickedFromCandidates }: {
  /** Existing template to edit, or null to create a new one.
   *
   * Accepts either shape (#2194): `RunTemplate` is the older per-kind
   * type, `UnifiedRunTemplate` is what `/run-templates` actually returns
   * and what every caller now hands over. Both carry the generation
   * fields this dialog reads, so the union is honest where the previous
   * `as unknown as` cast at each call site was not — a cast hides shape
   * drift between the two rather than reporting it. */
  template: RunTemplate | UnifiedRunTemplate | null
  /** All existing generation templates for this app (used only for the
   * legacy `isDefault` de-dup logic; callers still pass the app-level
   * list while the migration to variant-scoped templates is in progress). */
  allTemplates: RunTemplate[]
  appId: string
  /** Variant the new RunTemplate will be bound to. Required for #1114:
   * generation templates are now stored as RunTemplate documents scoped
   * to the variant, not as embedded objects on the Application. */
  variantId: string
  onClose: () => void
  onSaved: () => void
  /** Present when the dialog was opened by the Create Tests button rather than
   * from a list (#2175 slice 2). Swaps the footer for Run / Save and run.
   * Passing `settings` runs with what is on screen without saving it. */
  launch?: (settings?: Record<string, unknown>) => Promise<void> | void
  /** Present when the dialog was opened from a selection on the Proposed
   * tab (#2284), carrying how many suggestions were ticked.
   *
   * Two effects, both scoped to that entry point:
   *
   *   - `count` prefills Max test cases, so picking six does not run
   *     against a saved ceiling of one. Prefill only — the operator can
   *     change it before starting.
   *   - saving stops writing `pickedCandidateIds`. Those picks belong to
   *     this run and ride the trigger; writing them would change what
   *     every future trigger of a shared template does, including
   *     scheduled ones.
   *
   * Deliberately narrower than `launch`. The Runs page opens this dialog
   * with `launch` too, and there the candidate picker inside the dialog
   * IS template state that Save and run should persist. */
  pickedFromCandidates?: { count: number }
}) {
  const toast = useToast()
  const [name, setName] = useState(template?.name || '')
  const [description, setDescription] = useState(template?.description || '')

  // Agent Prompt — user-authored, app-specific instructions only. As of
  // #687 nothing is auto-stitched here (the old category / APP CONTEXT
  // primer was removed); the field starts empty on a fresh template.
  const [aiInstructions, setAiInstructions] = useState(template?.aiInstructions || '')

  // Defaults for a fresh template:
  //   - Max test cases: 1 (#1207 — operators creating their first
  //     Generation/Resume template should hit the fastest smoke-the-flow
  //     loop; multi-test runs obscure whether the basics work. They can
  //     dial up explicitly when they want more.)
  //   - Page timeout: 10s (matches replay-template default)
  //
  // Categories / Headed mode / Capture screenshots were retired in
  // Slice 1 of #857. The Agent Prompt is now the sole user-authored
  // steering channel; generation runs are always headless (no Xvfb in
  // the container); trace capture handles diagnostic screenshots.
  //
  // Floor at 1: legacy resume templates may have been saved with
  // maxTestCases=0 (the ottotester#221 sentinel that ottotester#235 retired).
  // Default of 1 lands on a usable value and the route validator (which
  // now requires >=1 on every run) never trips.
  // Prefilled from the selection when opened from the Proposed tab
  // (#2284), else the template's own value. Clamped the same way either
  // way, so a selection larger than the hard cap lands on the cap rather
  // than being sent as-is.
  /** The ceiling in force, or null until the server says (#2286). Every
   * use below has to cope with not knowing it yet: showing a number
   * that later changes is the thing this and #2284 both exist to stop. */
  const maxTestCasesCeiling = useMaxTestCasesPerRun()

  // Seeded without an upper clamp, because the ceiling is not known at
  // first render. The effect below trims it once it arrives, and the
  // server clamps whatever it is sent regardless.
  const [maxTestCases, setMaxTestCases] = useState(
    Math.max(pickedFromCandidates?.count ?? template?.maxTestCases ?? 1, 1),
  )

  // Trim to the ceiling the moment it lands, so a prefill above it does
  // not sit on screen as a number the run will not honour.
  useEffect(() => {
    if (maxTestCasesCeiling === null) return
    setMaxTestCases(prev => Math.min(prev, maxTestCasesCeiling))
  }, [maxTestCasesCeiling])
  const [playwrightTimeout, setPlaywrightTimeout] = useState((template?.playwrightTimeout ?? DEFAULT_PLAYWRIGHT_TIMEOUT_MS) / 1000)
  // #1076 — per-assertion polling timeout (`await expect(...)`).
  // Same shape + UI unit (ms) as the matching field on the replay
  // template editor so the two surfaces stay symmetric. The UI
  // surfaces this knob in milliseconds (not seconds) — Playwright's
  // docs, the trace report, and operator-facing surfacing all use
  // ms for assertion timeouts. Default 5000 matches Playwright's
  // built-in; legacy templates without the field coalesce to 5000.
  // #1076 follow-up: surface in SECONDS to match the sibling Page
  // timeout row (operators reading "Assertion timeout: 5000" next to
  // "Page timeout: 10" couldn't tell whether the units were mismatched
  // or mistyped). The schema and Playwright API still live in ms; we
  // convert at I/O boundaries (init: ms→s, save: s→ms).
  //
  // `Math.round` on init handles legacy templates saved under the
  // prior 500ms step (e.g., 5500ms → 6s) — without it the input would
  // surface fractional values that don't match the new integer step,
  // and the operator sees an "off" display. First save normalises.
  const [assertionTimeout, setAssertionTimeout] = useState(Math.round((template?.assertionTimeout ?? 5000) / 1000))
  // Pacing (#1642), stored in ms; between tests is shown in seconds.
  // 0 = no pause. A new template starts with the shared defaults; an
  // existing one shows what it stores, and a row without the field reads 0.
  const [interTestDelaySec, setInterTestDelaySec] = useState(
    Math.round((template ? template.interTestDelayMs ?? 0 : DEFAULT_INTER_TEST_DELAY_MS) / 1000),
  )
  const [slowMotionMs, setSlowMotionMs] = useState(
    template ? template.slowMotionMs ?? 0 : DEFAULT_SLOW_MOTION_MS,
  )
  const [captureTrace, setCaptureTrace] = useState<'off' | 'on-failure' | 'always'>(template?.captureTrace || 'on-failure')
  // Run-health reporting flags (#1009). Coalesce-`?? false` to match the
  // opt-in schema default: a template fetched without the field — or a
  // brand-new one — starts OFF. Turning a flag on forces full traces
  // (storage), so it's deliberately opt-in. Existing templates that stored
  // `true` keep it.
  const [reportConsoleErrors, setReportConsoleErrors] = useState(template?.reportConsoleErrors ?? false)
  const [reportNetworkErrors, setReportNetworkErrors] = useState(template?.reportNetworkErrors ?? false)
  // Plan is mandatory — generate/execute/heal all assume a plan was
  // already produced. Defend against a stored template whose phases
  // were saved before this rule landed by force-prepending 'plan'.
  // Heal is on by default for a fresh template (#687).
  const [phases, setPhases] = useState<PipelinePhase[]>(() => {
    const initial = template?.phases || ['plan', 'generate', 'execute', 'heal']
    return initial.includes('plan') ? initial : ['plan', ...initial]
  })
  const [logLevel, setLogLevel] = useState<LogLevel>(template?.logLevel || 'info')
  const [testDataMode, setTestDataMode] = useState<'off' | 'single-row' | 'all-rows'>(template?.testDataMode || 'off')
  // #1800 — "Add input variations": have the planner derive each fill field's
  // valid input classes and seed one Test Data row per class. Opt-in, off by
  // default (extra rows cost run time under All rows).
  const [addInputVariations, setAddInputVariations] = useState(template?.addInputVariations ?? false)
  // Feature Status (#687) — biases healer aggressiveness. Defaults to
  // 'established' so a fresh template (and any legacy template with no
  // stored value) keeps the baseline aggressive-heal behavior.
  const [stabilityExpectation, setStabilityExpectation] = useState<StabilityExpectation>(template?.stabilityExpectation ?? 'established')
  const [isDefault, setIsDefault] = useState(template?.isDefault ?? false)

  // Candidate tests inherited from this app (Slice 3C of #860 —
  // replaces the Slice-2 ref-doc-filename callout). Fetched on
  // mount so the read-only callout under the Agent Prompt textarea
  // can show the user what scaffolds the planner has available for
  // matching against their typed prompt at run time. Read-only
  // here — candidate management happens on the Application page's
  // Candidates tab (we link there). Reset to [] on every appId
  // change AND on fetch failure so a stale list from a previous
  // app can't leak into the current one (per Copilot review on
  // #866).
  const [candidates, setCandidates] = useState<CandidateTest[]>([])
  // #1452 + #1471 — ticked candidates in the picker. SAVED on the template as
  // `pickedCandidateIds`: each pick becomes a test linked back to its candidate
  // at run time. Seeded from the edited template so the selection round-trips;
  // reset when the app/variant changes.
  const [selectedCandidateIds, setSelectedCandidateIds] = useState<Set<string>>(
    new Set(template?.pickedCandidateIds ?? []),
  )
  // #1462 — for the no-candidates prompt hints: the app's domain label flavors
  // the worked example. Best-effort; absent it, the hints fall back to
  // generic copy.
  const [appDomain, setAppDomain] = useState<string | undefined>(undefined)
  // #1478 — per-candidate scripted tier in THIS variant, so the picker can
  // badge candidates that already have a test and stop offering them as fresh
  // picks. Keyed by candidate `_id`; absent key → treat as not-yet-scripted.
  // Best-effort: a failed fetch leaves the map empty (every candidate pickable).
  const [scriptedTierById, setScriptedTierById] = useState<Record<string, CandidateScriptedTier>>({})
  // #1626 — per-candidate "already covered by an existing test" (cosine ≥ the
  // duplicate threshold), fetched in the SAME scripted-status round-trip.
  // Best-effort: a failed fetch leaves it empty (no coverage badges).
  const [coverageByCandidate, setCoverageByCandidate] = useState<Record<string, { name: string; score: number }>>({})
  // #1462 — gate the picker-vs-hints choice on the candidates fetch having
  // resolved, so an app that HAS candidates doesn't briefly flash the hints
  // panel (candidates starts `[]`) before the picker swaps in (Copilot review).
  const [candidatesLoaded, setCandidatesLoaded] = useState(false)
  useEffect(() => {
    let cancelled = false
    setCandidates([])
    // Re-seed from the edited template (not blank) so a saved selection
    // survives an app/variant-driven refetch; a fresh template seeds empty.
    setSelectedCandidateIds(new Set(template?.pickedCandidateIds ?? []))
    setAppDomain(undefined)
    setScriptedTierById({})
    setCoverageByCandidate({})
    setCandidatesLoaded(false)
    applicationsAPI.listCandidates(appId).then(
      (list) => { if (!cancelled) { setCandidates(list); setCandidatesLoaded(true) } },
      () => { if (!cancelled) setCandidatesLoaded(true) /* empty → hints */ },
    )
    applicationsAPI.scriptedStatus(appId, variantId).then(
      (res) => {
        if (!cancelled) {
          setScriptedTierById(res.byCandidate ?? {})
          setCoverageByCandidate(res.coverageByCandidate ?? {})
        }
      },
      () => { /* swallow — without it every candidate stays pickable */ },
    )
    applicationsAPI.getById(appId).then(
      (app) => { if (!cancelled) setAppDomain(app?.applicationDomain) },
      () => { /* swallow — generic example without a domain */ },
    )
    return () => { cancelled = true }
  }, [appId, variantId])

  // #1486 — append a hint fragment/example into the prompt without running it
  // onto existing text. Shared by the no-candidate PromptHints chips and the
  // compact "Need ideas?" example shown when candidates exist.
  const insertPromptText = (text: string) =>
    setAiInstructions(prev => (!prev ? text : (/\s$/.test(prev) ? prev + text : `${prev} ${text}`)))

  // #1215 — wrapped in useAsyncAction so the Save button auto-disables
  // while the create/update POST is in-flight. The previous gate was
  // only `!name.trim()`, which let an operator double-click and fire
  // two POSTs (the second one usually rejected by a server-side
  // uniqueness constraint but the toast noise was ugly).
  /** Everything on screen that describes how the run behaves.
   *
   * Split out of the save action (#2175 slice 2) so the Run button can
   * send the same object without writing it — one builder means a field
   * added to the form reaches both paths. `isDefault` stays out: it says
   * which template this is, not how the run goes, and a per-run override
   * may not carry it. */
  function buildSettings(): RunTemplateSettings {
    return {
      name: name.trim(),
      description: description.trim() || undefined,
      phases,
      // #1478 — send the trimmed string (possibly empty), NOT `undefined`,
      // so clearing the prompt on an edit actually persists. `undefined`
      // would be dropped by the Mongoose update and the old prompt would
      // stick (same trap as `pickedCandidateIds`). The run-trigger guard
      // already treats an empty string as "no prompt".
      aiInstructions: aiInstructions.trim(),
      // #1471 — persist the picked candidates so each is created as a
      // linked test at run time. ALWAYS send an array (empty when nothing
      // is ticked): on an edit that deselects everything, sending
      // `undefined` would be ignored by the update (Mongoose drops
      // undefined paths) and the old picks would silently persist. An
      // explicit `[]` clears them. Downstream treats `[]` as "no picks".
      //
      // #2284 — EXCEPT when opened from a selection on the Proposed tab,
      // where the key is omitted entirely. Both of the above branches are
      // wrong there: this dialog's picker is empty on that path, so
      // sending `[]` would silently clear whatever the template had
      // saved, and seeding it would write this afternoon's picks onto a
      // template scheduled runs share. Omitting leaves the saved picks
      // untouched, which is the only outcome that does neither. The run's
      // own picks travel on the trigger instead.
      ...(pickedFromCandidates ? {} : { pickedCandidateIds: [...selectedCandidateIds] }),
      captureTrace,
      maxTestCases,
      playwrightTimeout: playwrightTimeout * 1000,
      // #1076 follow-up — both timeouts surface in seconds in the UI
      // and are converted to ms here for the schema. The round-then-
      // clamp normalises out-of-range typed values that may not have
      // been blurred before Save was clicked (the AssertionTimeoutRow's
      // onChange accepts any non-negative integer to keep the
      // "clear-then-type-larger" flow usable; final guard belongs here).
      assertionTimeout: Math.min(Math.max(Math.round(assertionTimeout) || 5, 1), 60) * 1000,
      // #1642 — s→ms, clamped to [0, 30] seconds (0 = no pause).
      interTestDelayMs: Math.min(Math.max(Math.round(interTestDelaySec) || 0, 0), 30) * 1000,
      // #1642 Slice 2 — per-action slow-motion (ms), already in ms.
      slowMotionMs: Math.max(Math.round(slowMotionMs) || 0, 0),
      logLevel,
      testDataMode,
      addInputVariations,
      stabilityExpectation,
      reportConsoleErrors,
      reportNetworkErrors,
    }
  }

  // #1215 — wrapped in useAsyncAction so the Save button auto-disables
  // while the create/update POST is in-flight. The previous gate was
  // only `!name.trim()`, which let an operator double-click and fire
  // two POSTs (the second one usually rejected by a server-side
  // uniqueness constraint but the toast noise was ugly).
  /** Tell the operator a save failed. Shared by the Save button's error
   * handler and Save and run, which needs the same message on the same
   * failure — two wordings for one failure is how they drift. */
  const reportSaveFailure = (err: unknown) =>
    toast({
      title: 'Failed to save template',
      description: toUserMessage(
        err,
        "Couldn't save the template. Try again — if the problem continues, check your connection.",
      ),
      rawDetails: extractRawDetails(err) ?? undefined,
      variant: 'error',
    })

  /** Write the form. Throws on failure so both callers can react —
   * `useAsyncAction` turns the throw into its error state, and Save and
   * run turns it into "do not start". */
  async function persistTemplate(): Promise<void> {
    if (!name.trim()) return
    // #1117: generation templates may be saved WITHOUT a prompt (drafts).
    // The non-empty-prompt requirement is enforced at run-trigger time, not
    // save — so there's NO aiInstructions block here. The empty state is
    // surfaced non-blockingly (the note below + a "Needs prompt" badge in
    // the runner list).

    // #1114 — generation templates are now first-class RunTemplate documents
    // stored via the `/run-templates` endpoint, not embedded in Application.
    // Build the patch payload — kind and variantId are fixed at creation and
    // never sent on update.
    const payload: UpdateRunTemplateInput = { ...buildSettings(), isDefault }
    if (template?.id) {
      await runTemplatesAPI.update(template.id, payload)
    } else {
      await runTemplatesAPI.create({
        ...payload,
        kind: 'generation',
        variantId,
        applicationId: appId,
      })
    }
  }

  const saveAction = useAsyncAction({
    run: persistTemplate,
    onSuccess: () => onSaved(),
    // Surface failures rather than swallowing them — a silent catch here
    // is what made the discriminator-strip bug read as "Save does nothing"
    // to the operator. Mirrors the error handling on the replay/resume
    // dialogs (#1149).
    onError: reportSaveFailure,
  })

  // Only meaningful when opened by Create Tests; the stand-in keeps
  // the hook call unconditional.
  const launcher = useRunTemplateLaunch({
    buildSettings,
    // NOT `saveAction.trigger` — that swallows the failure into its own
    // error state and resolves either way, so Save and run would start a
    // run against the old values after a save that did not land.
    save: async () => {
      try {
        await persistTemplate()
        onSaved()
        return true
      } catch (err) {
        reportSaveFailure(err)
        return false
      }
    },
    launch: launch ?? (() => {}),
  })

  // A generation run needs something to work from — a prompt or picks —
  // and the server rejects one that has neither. Gating the launch buttons
  // says so before the run starts rather than after it fails.
  //
  // #2284 — picks made on the Proposed tab count, even though this
  // dialog's own picker is empty on that path. They reach the run on the
  // trigger rather than through these settings, so reading only the
  // picker would disable both launch buttons for a run that has six
  // suggestions waiting and no prompt, which is the ordinary case there.
  const hasSomethingToRun =
    !!aiInstructions.trim() || selectedCandidateIds.size > 0 || (pickedFromCandidates?.count ?? 0) > 0

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{template ? 'Edit Test Authoring Template' : 'New Test Authoring Template'}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="space-y-2">
            <Label>Name</Label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Default" />
          </div>
          <div className="space-y-2">
            <Label>Description (optional)</Label>
            <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="Brief description" />
          </div>

            <div className="space-y-2">
              <Label>Phases</Label>
              <div className="flex gap-1">
                {(['plan', 'generate', 'execute', 'heal'] as const).map(p => {
                  // Plan is a prerequisite for every other phase, so the
                  // pill is rendered as always-on and non-interactive.
                  // Other phases stay toggle-able. Issue ottotester#175.
                  //
                  // Note: aria-disabled (not the HTML `disabled` attr) is
                  // intentional — `disabled` suppresses pointer + focus
                  // events on native buttons, which would also suppress
                  // the title-tooltip on hover. aria-disabled keeps the
                  // element interactive enough to surface the tooltip
                  // and keyboard-focus while we no-op the click.
                  const isPlan = p === 'plan'
                  const active = phases.includes(p)
                  return (
                    <button
                      key={p}
                      type="button"
                      aria-disabled={isPlan}
                      aria-pressed={active}
                      title={isPlan ? 'Plan is required — every other phase depends on it' : undefined}
                      onClick={isPlan ? undefined : () => setPhases(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p])}
                      className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'} ${isPlan ? 'cursor-default opacity-90' : ''}`}
                    >
                      {p.charAt(0).toUpperCase() + p.slice(1)}
                    </button>
                  )
                })}
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label htmlFor="aiHints">
                  Agent Prompt{selectedCandidateIds.size > 0 ? ' (optional)' : ''}
                </Label>
                {/* #1480 — one-click clear so the operator doesn't have to
                    select-all + delete to wipe a prompt carried over from a
                    saved template. Shown only when there's something to clear. */}
                {aiInstructions.trim().length > 0 && (
                  <button
                    type="button"
                    // Prevent the button from taking focus on click so a
                    // keyboard user stays in the textarea and can keep typing
                    // after clearing (matches SearchInput's clear affordance).
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => setAiInstructions('')}
                    className="ml-auto text-xs font-medium text-primary hover:underline"
                  >
                    Clear
                  </button>
                )}
              </div>
              {/* No aria-required (#1117): saving is allowed with an empty
                  prompt (draft), so marking it required would mislead AT.
                  #1486 — default to 2 lines (resizable) so the box doesn't
                  dominate the dialog and the hints below stay in view. The
                  placeholder adapts: with no candidate tests there's nothing
                  to "tick below", so it leads with a concrete example. Gated
                  on `candidatesLoaded` so an app that HAS candidates doesn't
                  flash the no-candidate example before the fetch resolves
                  (Copilot review). */}
              <Textarea id="aiHints" value={aiInstructions} onChange={e => setAiInstructions(e.target.value)}
                rows={2}
                placeholder={
                  !candidatesLoaded
                    ? 'Describe the tests you want…'
                    : candidates.length > 0
                      ? 'Optional — describe any extra tests beyond the ones you select below.'
                      : 'Describe the tests you want. e.g. “Create happy-path tests for sign-in and search, plus a negative test for a wrong password.”'}
              />
              {/* #1494 — worked-example block teaching what a good prompt
                  looks like (where to start / what to do / what should pass).
                  Shown only while the box is EMPTY (gone once the user types),
                  in both the candidate and no-candidate cases; gated on
                  `candidatesLoaded` so it doesn't appear before we know which
                  branch renders below. "Use this" sets the sentence. */}
              {candidatesLoaded && !aiInstructions.trim() && (
                <PromptExamples onUse={insertPromptText} />
              )}
              {/* #1478 + #1609 — when candidates are picked, the prompt is
                  optional. The picks SEED the plan: the planner plans and
                  verifies each one in a live browser (not an instant copy).
                  Say so plainly so the box doesn't read as a mandatory empty
                  stare, and so the operator knows a pick run plans like any
                  other. */}
              {selectedCandidateIds.size > 0 && (
                <p className="text-xs text-muted-foreground">
                  Your {selectedCandidateIds.size} selected {selectedCandidateIds.size === 1 ? 'test' : 'tests'} will
                  seed the plan — the planner plans and checks each one in a live browser. This prompt is optional — add
                  it only to ask for extra tests beyond your picks.
                </p>
              )}
              {/* #1117 + #1471: non-blocking. A generation template can be
                  saved as a draft. A run needs EITHER a prompt OR at least one
                  ticked test — so only warn when both are empty. */}
              {!aiInstructions.trim() && selectedCandidateIds.size === 0 && (
                <p className="flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400">
                  <span aria-hidden="true">⚠</span>
                  <span>
                    Nothing to run yet — you can save this as a draft, but a run won’t
                    start until you select at least one test below or describe the tests
                    you want in the box above.
                  </span>
                </p>
              )}

              {/* Candidate tests inherited from this app — tick the ones you
                  want made into tests (#1452 + #1471). Candidates are
                  priority-ordered (#1440); each ticked test is SAVED on the
                  template and created as its own test, linked back to the
                  candidate. The Agent Prompt box above is only for extra tests
                  beyond the ticked ones. */}
              {/* #2284 — hidden when the dialog was opened from a selection
                  on the Proposed tab. The picks for that run were already
                  made there and ride the trigger, and this panel's changes
                  are deliberately not saved, so leaving it interactive
                  would let an operator re-tick rows and watch it change
                  nothing. */}
              {pickedFromCandidates ? (
                <p className="text-xs text-muted-foreground">
                  Writing the {pickedFromCandidates.count === 1 ? 'proposed test' : `${pickedFromCandidates.count} proposed tests`} you
                  ticked. Close this and change the ticks to pick different ones.
                </p>
              ) : !candidatesLoaded ? null : candidates.length > 0 ? (
                <CandidatePickerPanel
                  candidates={candidates}
                  selectedIds={selectedCandidateIds}
                  onChange={setSelectedCandidateIds}
                  scriptedTierById={scriptedTierById}
                  coverageByCandidate={coverageByCandidate}
                />
              ) : (
                /* No candidate tests — tap-to-build chips + areas (#1462).
                   The worked example is suppressed (`hideExample`) because the
                   #1494 PromptExamples block above already shows examples. */
                <PromptHints
                  domain={appDomain}
                  hideExample
                  // Chip taps append (the shared helper handles the space so
                  // fragments don't run together); "Use this" replaces with
                  // the full worked example.
                  onInsert={insertPromptText}
                  onUseExample={text => setAiInstructions(text)}
                />
              )}
            </div>

            {/* Feature Status (#687) — biases how aggressively
                OttoTester's healer repairs failing tests. Sits
                directly under the Agent Prompt box per the #687
                design. */}
            <div className="space-y-2">
              <Label>Feature Status</Label>
              <div role="radiogroup" aria-label="Feature Status" className="space-y-1.5">
                {FEATURE_STATUS_OPTIONS.map(opt => {
                  const selected = stabilityExpectation === opt.value
                  return (
                    <div key={opt.value} className="flex items-center gap-1.5">
                      <button
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        onClick={() => setStabilityExpectation(opt.value)}
                        className="flex items-center gap-2 text-sm rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${selected ? 'border-primary' : 'border-muted-foreground'}`}>
                          {selected && <span className="w-2 h-2 rounded-full bg-primary" />}
                        </span>
                        <span>{opt.label}</span>
                      </button>
                      <Tooltip content={opt.tip} triggerLabel={`What "${opt.label}" means`}>
                        <Info className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                      </Tooltip>
                    </div>
                  )
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-xs">Max test cases</Label>
                <Input
                  type="number"
                  min={1}
                  {...(maxTestCasesCeiling !== null ? { max: maxTestCasesCeiling } : {})}
                  value={maxTestCases}
                  onChange={e => {
                    // parseInt returns NaN on empty/garbage and "1.9" → 1;
                    // fall back to the default rather than persist a
                    // non-finite value, then clamp on both sides so the
                    // server's [1, cap] guard never trips. #1207 — fallback
                    // is 1 (matching the form-default), down from 3.
                    const parsed = parseInt(e.target.value, 10)
                    const safe = Number.isFinite(parsed) ? parsed : 1
                    const floored = Math.max(safe, 1)
                    setMaxTestCases(maxTestCasesCeiling === null ? floored : Math.min(floored, maxTestCasesCeiling))
                  }}
                  className="w-24"
                />
                {maxTestCasesCeiling !== null && (
                  <p className="text-[10px] text-muted-foreground">Capped at {maxTestCasesCeiling}.</p>
                )}
              </div>
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-1.5">
                  Page timeout (sec)
                  <Tooltip
                    content="Maximum total time a single test can run before Playwright aborts it. Tune up when end-to-end test scenarios legitimately need longer (long form fills, long-running async work). Default 10."
                    triggerLabel="What is the page timeout?"
                    // Page timeout sits in column 2 (right) of both
                    // the discover-mode and resume-mode grids here
                    // (it's the 2nd grid child in each branch).
                    // `align='end'` opens the popover leftward,
                    // inward away from the dialog's right edge.
                    align="end"
                  >
                    <Info className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                  </Tooltip>
                </Label>
                <Input type="number" min={1} max={300} value={playwrightTimeout} onChange={e => setPlaywrightTimeout(clampNum(e.target.value, 1, 300, DEFAULT_PLAYWRIGHT_TIMEOUT_MS / 1000))} className="w-24" />
              </div>
              {/* #1076 — same row family as the page timeout above;
                  factored into a shared `AssertionTimeoutRow` so
                  the resume-mode branch above stays in lockstep
                  with the discover-mode branch here. */}
              <AssertionTimeoutRow value={assertionTimeout} onChange={setAssertionTimeout} />
            </div>
            {/* #1642 — pacing pair in its OWN 2-col grid so it's always a
                clean side-by-side row regardless of the timeout grid's cell
                count (this dialog has an extra maxTestCases cell, which would
                otherwise stagger the pair across rows). Tooltips open inward:
                tests (left) → start, steps (right) → end. */}
            <div className="grid grid-cols-2 gap-4">
              <BetweenTestsPauseRow value={interTestDelaySec} onChange={setInterTestDelaySec} align="start" />
              <BetweenStepsPauseRow value={slowMotionMs} onChange={setSlowMotionMs} align="end" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Trace capture</Label>
              <Select options={[{ value: 'off', label: 'Off' }, { value: 'on-failure', label: 'On failure (default)' }, { value: 'always', label: 'Always' }]}
                value={captureTrace} onChange={e => setCaptureTrace(e.target.value as any)} />
            </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label className="text-xs">Test data</Label>
              <Select options={[{ value: 'off', label: 'Off (default)' }, { value: 'single-row', label: 'Single row' }, { value: 'all-rows', label: 'All rows' }]}
                value={testDataMode} onChange={e => setTestDataMode(e.target.value as any)} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Activity log verbosity</Label>
              <Select options={[{ value: 'info', label: 'Info (default)' }, { value: 'debug', label: 'Debug' }, { value: 'trace', label: 'Trace' }]}
                value={logLevel} onChange={e => setLogLevel(e.target.value as LogLevel)} />
            </div>
          </div>

          {/* #1800 — "Add input variations". Sits under the Test data control
              because it fills the rows All rows runs. Off by default. */}
          <label className="flex items-start gap-2">
            <Checkbox checked={addInputVariations} onCheckedChange={v => setAddInputVariations(v === true)} />
            <span className="text-sm leading-snug">
              Add input variations
              <span className="block text-xs text-muted-foreground">
                For each field, add test-data rows covering the different kinds of values it accepts — dropdown options, smallest/largest numbers, and formats. Run with &lsquo;All rows&rsquo; to try them all.
              </span>
            </span>
          </label>

          {/* Issue reporting section (#1009 of meta #1008) — twin
              toggles that gate the run-health UI surfaces (row pill,
              Issues tab, run tile, dashboard trend) per-source.
              Both default ON; the same fields exist on the replay
              template editor so authors don't have to remember which
              template type owns the signal. */}
          <div className="space-y-2 rounded-md border border-border bg-muted/30 p-3">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Issue reporting</Label>
            <label className="flex items-start gap-2">
              <Checkbox checked={reportConsoleErrors} onCheckedChange={setReportConsoleErrors} />
              <span className="text-sm leading-snug">
                Report console errors
                <span className="block text-xs text-muted-foreground">
                  Surface uncaught <code>pageError</code>, <code>console.error</code>, and <code>console.warn</code> captured during runs from this template.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2">
              <Checkbox checked={reportNetworkErrors} onCheckedChange={setReportNetworkErrors} />
              <span className="text-sm leading-snug">
                Report network errors
                <span className="block text-xs text-muted-foreground">
                  Surface HTTP 4xx / 5xx and failed network requests captured during runs from this template.
                </span>
              </span>
            </label>
          </div>

          <label className="flex items-center gap-2">
            <Checkbox checked={isDefault} onCheckedChange={setIsDefault} />
            <span className="text-sm">Set as default template</span>
          </label>
        </DialogBody>
        {launch ? (
          <RunTemplateLaunchFooter
            busy={saveAction.isRunning || launcher.busy}
            canRun={!!name.trim() && hasSomethingToRun}
            onCancel={onClose}
            onRun={() => { void launcher.runNow() }}
            onSaveAndRun={() => { void launcher.saveAndRun() }}
          />
        ) : (
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saveAction.isRunning}>Cancel</Button>
          <Button
            onClick={saveAction.trigger}
            // Save is gated on a non-empty name (#175) and the in-flight
            // POST (#1215). The prompt is NOT required to save (#1117) —
            // generation templates can be saved as drafts; the prompt is
            // enforced at run-trigger time. The label softens to
            // "Save draft" ONLY when the template has nothing to run —
            // neither a prompt NOR any picked candidates (#1478). A
            // pick-and-go template (picks, empty prompt) is fully runnable,
            // so it reads "Save", not "Save draft".
            disabled={!name.trim() || saveAction.isRunning}
          >
            {saveAction.isRunning
              ? 'Saving…'
              : !aiInstructions.trim() && selectedCandidateIds.size === 0
                ? 'Save draft'
                : 'Save'}
          </Button>
        </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  )
}
